import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CheckCheck, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { formatDateLong } from '@/lib/format';

export const Route = createFileRoute('/alerts')({
  component: AlertsPage,
});

function AlertsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['alerts', { unread: false }], queryFn: () => api.listAlerts(false) });
  const dismissMut = useMutation({
    mutationFn: (id: string) => api.dismissAlert(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  });
  const markAllMut = useMutation({
    mutationFn: () => api.markAllAlertsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Alertes</h1>
          <p className="text-sm text-muted-foreground">Surveillance budgets, anomalies, jalons</p>
        </div>
        <Button variant="outline" onClick={() => markAllMut.mutate()}>
          <CheckCheck className="h-4 w-4" /> Tout marquer lu
        </Button>
      </div>

      <div className="space-y-3">
        {(data ?? []).map((a) => (
          <Card key={a.id} className={a.isRead ? 'opacity-70' : ''}>
            <CardContent className="flex items-start justify-between gap-4 p-4">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant={a.severity === 'critical' ? 'destructive' : a.severity === 'warning' ? 'warning' : 'secondary'}>
                    {a.severity}
                  </Badge>
                  <span className="font-semibold">{a.title}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{formatDateLong(a.createdAt)}</span>
                </div>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{a.message}</ReactMarkdown>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => dismissMut.mutate(a.id)} aria-label="Rejeter">
                <X className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
        {(!data || data.length === 0) && (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">Aucune alerte 👌</CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
